// Вітрина режимів навчання — адаптивна сітка карток із Premium-замками.
// Використовується на головній (Home) і у вкладці «Тести» (Categories),
// щоб не дублювати логіку старту режимів.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, type ExamMode } from '../lib/api';
import { haptic } from '../lib/telegram';
import { IconTile, LockBadge } from './AppIcons';

type IconKind = Parameters<typeof IconTile>[0]['kind'];

// Зрозуміле повідомлення з урахуванням офлайну (замість сухого «Не вдалося розпочати»)
function offlineAwareError(e: unknown, mode: ExamMode): string {
  if (e instanceof ApiError) return e.message;
  const m = e instanceof Error ? e.message : '';
  if (m === 'OFFLINE_PREMIUM_REQUIRED') return 'Офлайн доступний лише «Складай іспит». Решта режимів — онлайн.';
  if (m === 'OFFLINE_NO_QUESTIONS' || m === 'OFFLINE_NO_CATEGORY')
    return 'Питання ще не завантажені. Підключись до інтернету один раз.';
  if (!navigator.onLine) {
    if (mode === 'MISTAKES' || mode === 'BOOKMARKS') return 'Цей режим доступний лише онлайн.';
    return 'Немає інтернету, а база ще не готова. Підключись раз — і далі працюватиме офлайн.';
  }
  return 'Не вдалося розпочати';
}

type Props = {
  isPremium: boolean;
  mistakes: number;
  bookmarks: number;
};

export function ModeGrid({ isPremium, mistakes, bookmarks }: Props) {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function startMode(mode: ExamMode) {
    if (starting) return;
    setStarting(true);
    setErr(null);
    haptic.medium();
    try {
      const res = await api.startAttempt({ mode });
      const path = mode === 'OFFICIAL_20' ? `/exam/${res.attemptId}` : `/test/${res.attemptId}`;
      navigate(path, { state: res });
    } catch (e) {
      setErr(offlineAwareError(e, mode));
      haptic.error();
      setStarting(false);
    }
  }

  // Платний режим: не-Premium → ведемо на тарифи
  function protectedStart(mode: ExamMode) {
    if (!isPremium) {
      haptic.light();
      navigate('/premium');
      return;
    }
    void startMode(mode);
  }

  return (
    <div className="space-y-2">
      {err && (
        <div className="card border border-tg-destructive text-sm text-tg-destructive">{err}</div>
      )}

      {/* Головний БЕЗКОШТОВНИЙ режим — повна ширина, акцент */}
      <button
        onClick={() => startMode('OFFICIAL_20')}
        disabled={starting}
        className="card flex w-full items-center gap-3 text-left ring-1 ring-tg-button/40 transition active:scale-[0.99] disabled:opacity-60"
      >
        <IconTile kind="exam" className="h-12 w-12" glyphClass="h-7 w-7" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">
            Складай іспит
            <span className="ml-2 rounded bg-tg-success/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-tg-success">
              безкоштовно
            </span>
          </div>
          <div className="text-xs text-tg-hint">
            як в ТСЦ МВС: 20 запитань, 20 хвилин, макс. 2 помилки
          </div>
          {!isPremium && (
            <div className="mt-1 text-[11px] font-medium text-yellow-500">
              У безкоштовній версії відкрито 40% бази питань — повна база у Premium
            </div>
          )}
        </div>
        <span className="text-tg-hint">›</span>
      </button>

      {/* Сітка режимів */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
        <ModeCard
          icon="topics"
          title="Вчи по темах"
          subtitle="65 тем по порядку"
          locked={!isPremium}
          onClick={() => (isPremium ? navigate('/topics') : navigate('/premium'))}
        />
        <ModeCard
          icon="tickets"
          title="Білети"
          subtitle="20 випадкових, без часу"
          locked={!isPremium}
          disabled={starting}
          onClick={() => protectedStart('RANDOM_20')}
        />
        <ModeCard
          icon="hardest"
          title="100 найскладніших"
          subtitle="на яких валяться в ТСЦ"
          locked={!isPremium}
          disabled={starting}
          onClick={() => protectedStart('HARDEST_100')}
        />
        <ModeCard
          icon="mistakes"
          title="Мої помилки"
          subtitle={mistakes > 0 ? `${mistakes} для повторення` : 'Жодної 🎉'}
          locked={!isPremium}
          badge={isPremium && mistakes > 0 ? mistakes : undefined}
          disabled={starting || (isPremium && mistakes === 0)}
          onClick={() => protectedStart('MISTAKES')}
        />
        <ModeCard
          icon="bookmarks"
          title="Збережені"
          subtitle={bookmarks > 0 ? `${bookmarks} закладок` : 'Поки порожньо'}
          locked={!isPremium}
          badge={isPremium && bookmarks > 0 ? bookmarks : undefined}
          disabled={starting || (isPremium && bookmarks === 0)}
          onClick={() => protectedStart('BOOKMARKS')}
        />
        <ModeCard
          icon="signs"
          title="Дорожні знаки"
          subtitle="довідник 370+ знаків"
          locked={!isPremium}
          onClick={() => (isPremium ? navigate('/signs') : navigate('/premium'))}
        />
      </div>
    </div>
  );
}

type CardProps = {
  icon: IconKind;
  title: string;
  subtitle: string;
  locked?: boolean;
  badge?: number;
  disabled?: boolean;
  onClick?: () => void;
  to?: string;
};

function ModeCard(p: CardProps) {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <IconTile kind={p.icon} dimmed={p.locked} />
        {p.locked ? (
          <LockBadge />
        ) : p.badge !== undefined ? (
          <span className="rounded-full bg-tg-button px-2 py-0.5 text-xs font-semibold text-tg-button-text">
            {p.badge}
          </span>
        ) : null}
      </div>
      <div className="mt-2 min-w-0">
        <div className="text-sm font-medium leading-tight">{p.title}</div>
        <div className="mt-0.5 text-xs leading-snug text-tg-hint">{p.subtitle}</div>
      </div>
      {p.locked && (
        <div className="mt-auto pt-2 text-xs font-semibold text-tg-button">Активувати Premium →</div>
      )}
    </>
  );
  const cls = `card flex h-full flex-col text-left transition active:scale-[0.99] ${
    p.disabled ? 'opacity-60' : ''
  }`;
  if (p.to) {
    return (
      <Link to={p.to} onClick={() => haptic.light()} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button onClick={p.onClick} disabled={p.disabled} className={`${cls} disabled:opacity-50`}>
      {inner}
    </button>
  );
}
